/**
 * TODO: move to microfrontend-utils?
 * A simple EventBus implementation using the DOM's EventTarget and AbortController
 * This maximizes compatibility, and requires no dependencies.
 */
export default class EventBus {
    /**
     * @param {AbortController} [abortController]
     */
    constructor(abortController = new AbortController()) {
        this.eventTarget = new EventTarget()
        this.abortController = abortController
        /**
         * @type {Map<string, WeakMap>}
         */
        this.eventStore = new Map()
    }

    /**
     * @param {string} event
     * @param {any} payload
     */
    dispatch(event, payload = {}) {
        if (!this.eventTarget) {
            throw new Error(
                'EventBus has been destroyed and can no longer dispatch events.'
            )
        }
        this.eventTarget.dispatchEvent(
            new CustomEvent(event, { detail: payload })
        )
    }

    /**
     * Add an event listener
     * @param {string} event
     * @param {function} callback
     * @returns {function} removeEventListener function
     */
    addEventListener(event, callback) {
        if (!this.eventStore.has(event)) {
            this.eventStore.set(event, new WeakMap())
        }
        function wrappedCallback(e) {
            callback(e.detail)
        }
        this.eventStore.get(event).set(callback, wrappedCallback)
        this.eventTarget.addEventListener(event, wrappedCallback, {
            signal: this.abortController.signal,
        })

        return this.removeEventListener.bind(this, event, callback)
    }

    /**
     * Remove an event listener
     * @param event
     * @param callback
     */
    removeEventListener(event, callback) {
        const wrappedCallback = this.eventStore.get(event)?.get(callback)
        if (!wrappedCallback) {
            return
        }
        this.eventTarget.removeEventListener(event, wrappedCallback)
        // Clean up the entry from eventStore to prevent memory leaks
        this.eventStore.get(event)?.delete?.(callback)
    }

    /**
     * Abort all event listeners, and prepare for GC.
     */
    destroy() {
        if (this.abortController) {
            this.abortController.abort()
        }
        if (this.eventStore) {
            this.eventStore.clear()
        }
        this.eventStore = null
        this.eventTarget = null
        this.abortController = null
    }
}
